import { useState, useEffect } from "react";
import Button from "../../Components/Button/Button";
import TextField from "../../Components/TextField/TextField";
import Dropdown from "../../Components/Dropdown/Dropdown";
import { useToast } from "../../Contexts/ToastContext/ToastContext";
import { useAuth } from "../../Contexts/AuthContext/AuthContext";
import api from "../../services/api";
import "./Users.css";

const Users = () => {
  const { showToast } = useToast();
  const { user: currentUser, isAdmin, isManager, canCreateUser } = useAuth();

  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [allRooms, setAllRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRole, setFilterRole] = useState("all");

  const [showUserModal, setShowUserModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [userForm, setUserForm] = useState({
    username: "",
    password: "",
    role: "viewer",
    managerScope: null,
    assignedLocations: [],
    assignedWarehouses: [],
    assignedRooms: []
  });

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await api.getUsers(currentPage, pageLimit);
      setUsers(data.users || []);
      setTotalUsers(data.totalUsers || 0);
    } catch (error) {
      showToast(error.message || "Failed to fetch users", 3000, true, "danger");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const data = await api.getLocations(1, 100);
      setLocations(data.locations || []);
    } catch (error) {
      console.error("Failed to fetch locations:", error);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const data = await api.getWarehouses(1, 100);
      setWarehouses(data.warehouses || []);
    } catch (error) {
      console.error("Failed to fetch warehouses:", error);
    }
  };

  const fetchRooms = async () => {
    try {
      const data = await api.getRooms();
      setAllRooms(data.rooms || []);
    } catch (error) {
      console.error("Failed to fetch rooms:", error);
    }
  };

  const createUser = async () => {
    if (!userForm.username || !userForm.password) {
      showToast("Username and password are required", 2000, true, "danger");
      return;
    }

    // Validate manager requirements
    if (userForm.role === "manager") {
      if (!userForm.managerScope) {
        showToast("Manager scope is required", 2000, true, "danger");
        return;
      }
      if (userForm.managerScope === "location" && userForm.assignedLocations.length === 0) {
        showToast("Location-scoped manager requires at least one assigned location", 2000, true, "danger");
        return;
      }
      if (userForm.managerScope === "warehouse" && userForm.assignedWarehouses.length === 0) {
        showToast("Warehouse-scoped manager requires at least one assigned warehouse", 2000, true, "danger");
        return;
      }
    }

    // Validate worker/viewer requirements
    if (["worker", "viewer"].includes(userForm.role) && userForm.assignedRooms.length === 0) {
      showToast(`${userForm.role} requires at least one assigned partition`, 2000, true, "danger");
      return;
    }

    try {
      await api.register(userForm);
      showToast("User created successfully!", 2000, true, "success");
      setShowUserModal(false);
      resetUserForm();
      fetchUsers();
    } catch (error) {
      showToast(error.message || "Failed to create user", 2000, true, "danger");
      console.error(error);
    }
  };

  const updateUser = async () => {
    if (!userForm.username) {
      showToast("Username is required", 2000, true, "danger");
      return;
    }

    try {
      const updateData = { ...userForm };
      if (!updateData.password) {
        delete updateData.password;
      }

      await api.updateUser(editingUser._id, updateData);
      showToast("User updated successfully!", 2000, true, "success");
      setShowUserModal(false);
      setEditingUser(null);
      resetUserForm();
      fetchUsers();
    } catch (error) {
      showToast(error.message || "Failed to update user", 2000, true, "danger");
      console.error(error);
    }
  };

  const deleteUser = async () => {
    if (!deleteTarget) return;

    try {
      await api.deleteUser(deleteTarget._id);
      showToast("User deleted successfully!", 2000, true, "success");
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
      fetchUsers();
    } catch (error) {
      showToast(error.message || "Failed to delete user", 2000, true, "danger");
      console.error(error);
    }
  };

  const resetUserForm = () => {
    setUserForm({
      username: "",
      password: "",
      role: isManager() ? "worker" : "viewer",
      managerScope: null,
      assignedLocations: [],
      assignedWarehouses: [],
      assignedRooms: []
    });
  };

  useEffect(() => {
    fetchUsers();
    fetchLocations();
    fetchWarehouses();
    fetchRooms();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [currentPage, pageLimit]);

  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchTerm || (user.username || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === "all" || user.role === filterRole;
    return matchesSearch && matchesRole;
  });

  const totalPages = Math.ceil(totalUsers / pageLimit);

  const getRoleBadgeStyle = (role) => {
    switch (role) {
      case "admin":
        return { backgroundColor: "#fee2e2", color: "#dc2626", border: "1px solid #fca5a5" };
      case "operator":
        return { backgroundColor: "#ede9fe", color: "#7c3aed", border: "1px solid #c4b5fd" };
      case "cashier":
        return { backgroundColor: "#dcfce7", color: "#166534", border: "1px solid #86efac" };
      case "manager":
        return { backgroundColor: "#fef3c7", color: "#d97706", border: "1px solid #fcd34d" };
      case "worker":
        return { backgroundColor: "#d1fae5", color: "#059669", border: "1px solid #6ee7b7" };
      default:
        return { backgroundColor: "#e0f2fe", color: "#0284c7", border: "1px solid #7dd3fc" };
    }
  };

  const roleOptions = [
    { label: "All Roles", value: "all" },
    { label: "Admin", value: "admin" },
    { label: "Operator", value: "operator" },
    { label: "Cashier", value: "cashier" },
    { label: "Manager", value: "manager" },
    { label: "Worker", value: "worker" },
    { label: "Viewer", value: "viewer" }
  ];

  // Role options for the form depend on the current user's role
  const getRoleFormOptions = () => {
    if (isAdmin()) {
      return [
        { label: "Admin", value: "admin" },
        { label: "Operator", value: "operator" },
        { label: "Cashier", value: "cashier" },
        { label: "Manager", value: "manager" },
        { label: "Worker", value: "worker" },
        { label: "Viewer", value: "viewer" }
      ];
    }
    // Managers can only create workers and viewers
    return [
      { label: "Worker", value: "worker" },
      { label: "Viewer", value: "viewer" }
    ];
  };

  const managerScopeOptions = [
    { label: "Location-scoped", value: "location" },
    { label: "Warehouse-scoped", value: "warehouse" }
  ];

  const getRoomHierarchyLabel = (room) => {
    const warehouseName = room.warehouseId?.name;
    const locationName = room.locationId?.name
      || locations.find(l => l._id === (room.locationId?._id || room.locationId
        || room.warehouseId?.locationId?._id || room.warehouseId?.locationId))?.name;

    if (locationName && warehouseName) return `${locationName} → ${warehouseName}`;
    if (warehouseName) return warehouseName;
    return `Partition ${room.rid}`;
  };

  const handleRoleChange = (newRole) => {
    setUserForm({
      ...userForm,
      role: newRole,
      managerScope: newRole === "manager" ? "location" : null,
      assignedLocations: [],
      assignedWarehouses: [],
      assignedRooms: []
    });
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-content">
        <div className="dashboard-header">
          <div>
            <h1 className="dashboard-title">User Management</h1>
            <p className="dashboard-subtitle">
              {isAdmin() ? "Manage all system users and permissions" : "Manage workers and viewers in your scope"}
            </p>
          </div>
          <div className="dfcc gap-2">
            <Button
              value="Add User"
              iconLeft="fa-plus"
              onClick={() => {
                setEditingUser(null);
                resetUserForm();
                setShowUserModal(true);
              }}
              variant="primary"
              padding="12px 24px"
            />
          </div>
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: "#e3f2fd" }}>
              <i className="fa fa-users" style={{ color: "#2196F3" }}></i>
            </div>
            <div className="stat-content">
              <h3 className="stat-value">{totalUsers}</h3>
              <p className="stat-label">Total Users</p>
            </div>
          </div>

          {isAdmin() && (
            <div className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: "#fee2e2" }}>
                <i className="fa fa-shield" style={{ color: "#dc2626" }}></i>
              </div>
              <div className="stat-content">
                <h3 className="stat-value">{users.filter(u => u.role === "admin").length}</h3>
                <p className="stat-label">Admins</p>
              </div>
            </div>
          )}

          {isAdmin() && (
            <div className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: "#ede9fe" }}>
                <i className="fa fa-cogs" style={{ color: "#7c3aed" }}></i>
              </div>
              <div className="stat-content">
                <h3 className="stat-value">{users.filter(u => u.role === "operator").length}</h3>
                <p className="stat-label">Operators</p>
              </div>
            </div>
          )}

          {isAdmin() && (
            <div className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: "#fef3c7" }}>
                <i className="fa fa-user-secret" style={{ color: "#d97706" }}></i>
              </div>
              <div className="stat-content">
                <h3 className="stat-value">{users.filter(u => u.role === "manager").length}</h3>
                <p className="stat-label">Managers</p>
              </div>
            </div>
          )}

          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: "#d1fae5" }}>
              <i className="fa fa-wrench" style={{ color: "#059669" }}></i>
            </div>
            <div className="stat-content">
              <h3 className="stat-value">{users.filter(u => u.role === "worker").length}</h3>
              <p className="stat-label">Workers</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: "#e0f2fe" }}>
              <i className="fa fa-eye" style={{ color: "#0284c7" }}></i>
            </div>
            <div className="stat-content">
              <h3 className="stat-value">{users.filter(u => u.role === "viewer").length}</h3>
              <p className="stat-label">Viewers</p>
            </div>
          </div>
        </div>

        <div className="filters-section">
          <h2 className="section-title">Search & Filter Users</h2>
          <div className="filters-grid">
            <TextField
              placeholder="Search by username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              iconLeft="fa-search"
              variant="outlined"
            />
            <Dropdown
              options={roleOptions}
              value={filterRole}
              onChange={setFilterRole}
              placeholder="Select Role"
              variant="outlined"
            />
            <Button
              value="Clear"
              iconLeft="fa-times"
              onClick={() => {
                setSearchTerm("");
                setFilterRole("all");
              }}
              variant="outline"
              padding="12px 24px"
            />
          </div>
        </div>

        <div className="table-section">
          <div className="section-header">
            <h2 className="section-title">Users List</h2>
            <span className="results-count">
              Showing {filteredUsers.length} of {totalUsers} users
            </span>
          </div>

          <div className="table-container">
            {loading ? (
              <div className="loading-state">
                <i className="fa fa-spinner fa-spin" style={{ fontSize: "48px", color: "#3B82F6" }}></i>
                <p>Loading users...</p>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="empty-state">
                <i className="fa fa-users" style={{ fontSize: "48px", color: "#ccc" }}></i>
                <p>No users found</p>
              </div>
            ) : (
              <table className="tags-table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Role</th>
                    <th>Scope / Assignments</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user._id}>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                          <div style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "10px",
                            backgroundColor: "#e0f2fe",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                          }}>
                            <i className="fa fa-user" style={{ color: "#0284c7" }}></i>
                          </div>
                          <div>
                            <span style={{ fontWeight: "600", color: "#0F172A" }}>{user.username}</span>
                            {user._id === currentUser?.id && (
                              <span style={{
                                marginLeft: "8px",
                                fontSize: "10px",
                                padding: "2px 6px",
                                backgroundColor: "#dbeafe",
                                color: "#3B82F6",
                                borderRadius: "4px",
                                fontWeight: "600"
                              }}>YOU</span>
                            )}
                            {!user.isActive && (
                              <span style={{
                                marginLeft: "8px",
                                fontSize: "10px",
                                padding: "2px 6px",
                                backgroundColor: "#fee2e2",
                                color: "#dc2626",
                                borderRadius: "4px",
                                fontWeight: "600"
                              }}>INACTIVE</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <span style={{
                            display: "inline-block",
                            padding: "6px 12px",
                            borderRadius: "8px",
                            fontSize: "12px",
                            fontWeight: "600",
                            textTransform: "uppercase",
                            ...getRoleBadgeStyle(user.role)
                          }}>
                            {user.role}
                          </span>
                          {user.role === "manager" && user.managerScope && (
                            <span style={{
                              fontSize: "10px",
                              color: "#64748b",
                              fontStyle: "italic"
                            }}>
                              {user.managerScope}-scoped
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                          {user.assignedLocations?.length > 0 && (
                            <span style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                              padding: "4px 8px",
                              borderRadius: "6px",
                              backgroundColor: "#e8f5e9",
                              color: "#2e7d32",
                              fontSize: "11px",
                              fontWeight: "600"
                            }}>
                              <i className="fa fa-map-marker"></i>
                              {user.assignedLocations.length} location{user.assignedLocations.length > 1 ? 's' : ''}
                            </span>
                          )}
                          {user.assignedWarehouses?.length > 0 && (
                            <span style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                              padding: "4px 8px",
                              borderRadius: "6px",
                              backgroundColor: "#fff3e0",
                              color: "#e65100",
                              fontSize: "11px",
                              fontWeight: "600"
                            }}>
                              <i className="fa fa-building"></i>
                              {user.assignedWarehouses.length} warehouse{user.assignedWarehouses.length > 1 ? 's' : ''}
                            </span>
                          )}
                          {user.assignedRooms?.length > 0 && (
                            <span style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                              padding: "4px 8px",
                              borderRadius: "6px",
                              backgroundColor: "#e0e7ff",
                              color: "#4338ca",
                              fontSize: "11px",
                              fontWeight: "600"
                            }}>
                              <i className="fa fa-cube"></i>
                              {user.assignedRooms.length} partition{user.assignedRooms.length > 1 ? 's' : ''}
                            </span>
                          )}
                          {!user.assignedLocations?.length &&
                           !user.assignedWarehouses?.length &&
                           !user.assignedRooms?.length && (
                            <span style={{ color: "#94a3b8", fontSize: "12px" }}>
                              {user.role === 'admin' || user.role === 'operator' ? 'Full access' : 'No assignments'}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="timestamp-cell">
                        {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-'}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="action-btn edit-btn"
                            onClick={() => {
                              setEditingUser(user);
                              setUserForm({
                                username: user.username,
                                password: "",
                                role: user.role,
                                managerScope: user.managerScope || null,
                                assignedLocations: (user.assignedLocations || []).map(l => l._id || l),
                                assignedWarehouses: (user.assignedWarehouses || []).map(w => w._id || w),
                                assignedRooms: user.assignedRooms || []
                              });
                              setShowUserModal(true);
                            }}
                            title="Edit User"
                            disabled={!isAdmin() && user.role !== 'worker' && user.role !== 'viewer'}
                            style={!isAdmin() && user.role !== 'worker' && user.role !== 'viewer' ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                          >
                            <i className="fa fa-edit"></i>
                          </button>
                          <button
                            className="action-btn delete-btn"
                            onClick={() => {
                              setDeleteTarget(user);
                              setShowDeleteConfirm(true);
                            }}
                            title="Delete User"
                            disabled={user._id === currentUser?.id || (!isAdmin() && !['worker', 'viewer'].includes(user.role))}
                            style={(user._id === currentUser?.id || (!isAdmin() && !['worker', 'viewer'].includes(user.role))) ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                          >
                            <i className="fa fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {!loading && totalUsers > 0 && (
            <div className="pagination">
              <Button
                value="Previous"
                iconLeft="fa-chevron-left"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                variant="outline"
                padding="8px 16px"
              />
              <div className="pagination-info">
                Page {currentPage} of {totalPages}
              </div>
              <Button
                value="Next"
                iconRight="fa-chevron-right"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                variant="outline"
                padding="8px 16px"
              />
            </div>
          )}
        </div>
      </div>

      {showUserModal && (
        <div className="modal-overlay" onClick={() => setShowUserModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-wrapper">
              <div className="modal-icon-circle" style={{ background: "linear-gradient(135deg, #E0F2FE 0%, #BAE6FD 100%)", color: "#0284c7" }}>
                <i className="fa fa-user"></i>
              </div>
              <div className="modal-title-section">
                <span className="modal-title">{editingUser ? "Edit User" : "Add New User"}</span>
                <span className="modal-subtitle">
                  {editingUser ? "Update user information and permissions" : "Create a new user account"}
                </span>
              </div>
              <div className="modal-close-btn" onClick={() => setShowUserModal(false)}>
                <i className="fa fa-times"></i>
              </div>
            </div>

            <div className="modal-body-wrapper">
              <div className="form-field">
                <div className="form-label-wrapper">
                  <span className="form-label">Username</span>
                  <span className="form-label-required">*</span>
                </div>
                <TextField
                  type="text"
                  placeholder="Enter username"
                  value={userForm.username}
                  onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                  variant="outlined"
                />
              </div>

              <div className="form-field">
                <div className="form-label-wrapper">
                  <span className="form-label">Password</span>
                  {!editingUser && <span className="form-label-required">*</span>}
                  {editingUser && <span className="form-label-hint">(leave blank to keep current)</span>}
                </div>
                <TextField
                  type="password"
                  placeholder={editingUser ? "Enter new password" : "Enter password"}
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  variant="outlined"
                />
              </div>

              <div className="form-field">
                <div className="form-label-wrapper">
                  <span className="form-label">Role</span>
                </div>
                <Dropdown
                  options={getRoleFormOptions()}
                  value={userForm.role}
                  onChange={handleRoleChange}
                  variant="outlined"
                  disabled={editingUser && !isAdmin()}
                />
              </div>

              {/* Manager scope selection (admin only) */}
              {userForm.role === "manager" && isAdmin() && (
                <div className="form-field">
                  <div className="form-label-wrapper">
                    <span className="form-label">Manager Scope</span>
                    <span className="form-label-required">*</span>
                  </div>
                  <Dropdown
                    options={managerScopeOptions}
                    value={userForm.managerScope || "location"}
                    onChange={(val) => setUserForm({
                      ...userForm,
                      managerScope: val,
                      assignedLocations: [],
                      assignedWarehouses: []
                    })}
                    variant="outlined"
                  />
                  <span className="form-label-hint" style={{ marginTop: "4px", display: "block" }}>
                    {userForm.managerScope === "location"
                      ? "Can manage warehouses and rooms within assigned locations"
                      : "Can manage rooms within assigned warehouses"}
                  </span>
                </div>
              )}

              {/* Location-scoped manager assignments */}
              {userForm.role === "manager" && userForm.managerScope === "location" && (
                <div className="form-field">
                  <div className="form-label-wrapper">
                    <span className="form-label">Assigned Locations</span>
                    <span className="form-label-required">*</span>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "8px", maxHeight: "150px", overflowY: "auto" }}>
                    {locations.map(loc => (
                      <label key={loc._id} style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "8px 12px",
                        borderRadius: "8px",
                        backgroundColor: userForm.assignedLocations.includes(loc._id) ? "#e8f5e9" : "#f1f5f9",
                        border: userForm.assignedLocations.includes(loc._id) ? "2px solid #4caf50" : "2px solid transparent",
                        cursor: "pointer",
                        fontSize: "13px"
                      }}>
                        <input
                          type="checkbox"
                          checked={userForm.assignedLocations.includes(loc._id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setUserForm({ ...userForm, assignedLocations: [...userForm.assignedLocations, loc._id] });
                            } else {
                              setUserForm({ ...userForm, assignedLocations: userForm.assignedLocations.filter(id => id !== loc._id) });
                            }
                          }}
                        />
                        <i className="fa fa-map-marker" style={{ color: "#4caf50" }}></i>
                        {loc.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Warehouse-scoped manager assignments */}
              {userForm.role === "manager" && userForm.managerScope === "warehouse" && (
                <div className="form-field">
                  <div className="form-label-wrapper">
                    <span className="form-label">Assigned Warehouses</span>
                    <span className="form-label-required">*</span>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "8px", maxHeight: "150px", overflowY: "auto" }}>
                    {warehouses.map(wh => (
                      <label key={wh._id} style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "8px 12px",
                        borderRadius: "8px",
                        backgroundColor: userForm.assignedWarehouses.includes(wh._id) ? "#fff3e0" : "#f1f5f9",
                        border: userForm.assignedWarehouses.includes(wh._id) ? "2px solid #ff9800" : "2px solid transparent",
                        cursor: "pointer",
                        fontSize: "13px"
                      }}>
                        <input
                          type="checkbox"
                          checked={userForm.assignedWarehouses.includes(wh._id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setUserForm({ ...userForm, assignedWarehouses: [...userForm.assignedWarehouses, wh._id] });
                            } else {
                              setUserForm({ ...userForm, assignedWarehouses: userForm.assignedWarehouses.filter(id => id !== wh._id) });
                            }
                          }}
                        />
                        <i className="fa fa-building" style={{ color: "#ff9800" }}></i>
                        {wh.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Worker and Viewer partition assignments */}
              {["worker", "viewer"].includes(userForm.role) && (
                <div className="form-field">
                  <div className="form-label-wrapper">
                    <span className="form-label">Assigned Partitions</span>
                    <span className="form-label-required">*</span>
                  </div>
                  <span className="form-label-hint" style={{ marginBottom: "8px", display: "block" }}>
                    {userForm.role === "worker"
                      ? "Worker can manage tags in these partitions"
                      : "Viewer can only view tags in these partitions"}
                  </span>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "8px", maxHeight: "200px", overflowY: "auto" }}>
                    {allRooms.map(room => (
                      <label key={room.rid} style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "8px 12px",
                        borderRadius: "8px",
                        backgroundColor: userForm.assignedRooms.includes(room.rid) ? "#e0e7ff" : "#f1f5f9",
                        border: userForm.assignedRooms.includes(room.rid) ? "2px solid #6366f1" : "2px solid transparent",
                        cursor: "pointer",
                        fontSize: "13px"
                      }}>
                        <input
                          type="checkbox"
                          checked={userForm.assignedRooms.includes(room.rid)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setUserForm({ ...userForm, assignedRooms: [...userForm.assignedRooms, room.rid] });
                            } else {
                              setUserForm({ ...userForm, assignedRooms: userForm.assignedRooms.filter(id => id !== room.rid) });
                            }
                          }}
                        />
                        <i className="fa fa-cube" style={{ color: "#6366f1" }}></i>
                        {room.roomTitle}
                        <span style={{ color: "#94a3b8", fontSize: "11px" }}>
                          ({getRoomHierarchyLabel(room)})
                        </span>
                      </label>
                    ))}
                    {allRooms.length === 0 && (
                      <span style={{ color: "#94a3b8", fontSize: "12px" }}>No rooms available</span>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer-wrapper">
              <div className="modal-action-btn cancel-btn" onClick={() => setShowUserModal(false)}>
                <i className="fa fa-times"></i>
                <span>Cancel</span>
              </div>
              <div
                className="modal-action-btn primary-btn"
                onClick={editingUser ? updateUser : createUser}
              >
                <i className={`fa ${editingUser ? "fa-save" : "fa-plus"}`}></i>
                <span>{editingUser ? "Update User" : "Create User"}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && deleteTarget && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-container delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-wrapper delete-header">
              <div className="modal-icon-circle delete-icon-bg">
                <i className="fa fa-exclamation-triangle"></i>
              </div>
              <div className="modal-title-section">
                <span className="modal-title delete-title">Delete User</span>
                <span className="modal-subtitle delete-subtitle">
                  This action cannot be undone
                </span>
              </div>
              <div className="modal-close-btn delete-close" onClick={() => setShowDeleteConfirm(false)}>
                <i className="fa fa-times"></i>
              </div>
            </div>

            <div className="modal-body-wrapper delete-body">
              <div className="delete-message-wrapper">
                <div className="delete-message-icon">
                  <i className="fa fa-user-times"></i>
                </div>
                <div className="delete-message-content">
                  <span className="delete-message-primary">
                    Are you sure you want to delete <strong>"{deleteTarget.username}"</strong>?
                  </span>
                  <span className="delete-message-secondary">
                    This will permanently remove this user account and revoke their access.
                  </span>
                </div>
              </div>
            </div>

            <div className="modal-footer-wrapper">
              <div
                className="modal-action-btn cancel-btn"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteTarget(null);
                }}
              >
                <i className="fa fa-times"></i>
                <span>Cancel</span>
              </div>
              <div
                className="modal-action-btn delete-btn"
                onClick={deleteUser}
              >
                <i className="fa fa-trash"></i>
                <span>Delete User</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
